import { request } from '@umijs/max'
import type { ApiResponseVO, PaginatedResponseVO } from '@/common/types'
import type {
  UserVO,
  SuperLoginDto,
  SuperCreateUserDto,
  SuperUpdateUserDto,
  SuperUserListQueryDto,
} from './types'

/**
 * 超管端 sys/user 相关 API
 * 统一使用 ApiResponseVO / PaginatedResponseVO
 */
export const sysUserApi = {
  /**
   * 超管登录（仅 role=super 可登录）
   * POST /super/auth/login
   */
  async superLogin(
    body: SuperLoginDto,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<{ token: string }>> {
    return request<ApiResponseVO<{ token: string }>>('/super/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    })
  },

  /**
   * 获取当前登录用户信息
   * GET /users/info
   */
  async getCurrentUser(options?: {
    [key: string]: any
  }): Promise<ApiResponseVO<UserVO>> {
    return request<ApiResponseVO<UserVO>>('/users/info', {
      method: 'GET',
      ...(options || {}),
    })
  },

  /**
   * 创建用户（超管）
   * POST /super/users/create
   */
  async createUser(
    body: SuperCreateUserDto,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<UserVO>> {
    return request<ApiResponseVO<UserVO>>('/super/users/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    })
  },

  /**
   * 获取用户详情（超管）
   * GET /super/users/:id
   */
  async getUserById(
    id: number,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<UserVO>> {
    return request<ApiResponseVO<UserVO>>(`/super/users/${id}`, {
      method: 'GET',
      ...(options || {}),
    })
  },

  /**
   * 更新用户（超管）
   * PATCH /super/users/:id/update
   */
  async updateUser(
    id: number,
    body: SuperUpdateUserDto,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<UserVO>> {
    return request<ApiResponseVO<UserVO>>(`/super/users/${id}/update`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    })
  },

  /**
   * 删除用户（超管，软删除）
   * DELETE /super/users/:id
   */
  async removeUser(
    id: number,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<void>> {
    return request<ApiResponseVO<void>>(`/super/users/${id}`, {
      method: 'DELETE',
      ...(options || {}),
    })
  },

  /**
   * 分页获取用户列表（超管）
   * POST /super/users/list
   */
  async listUsers(
    body: SuperUserListQueryDto,
    options?: { [key: string]: any },
  ): Promise<ApiResponseVO<PaginatedResponseVO<UserVO>>> {
    return request<ApiResponseVO<PaginatedResponseVO<UserVO>>>(
      '/super/users/list',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: body,
        ...(options || {}),
      },
    )
  },
}
